import React, { Component } from "react";
import {
  MDBBtn,
  MDBInput,
  MDBSwitch,
  MDBCollapse,
} from "mdb-react-ui-kit";
import "./AdminActorDetail.css";

import { doFetch } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";
import { MfmImageUploader } from "Projects/CC_cubi_clan_project/GlobalComponents/MfmImageUploader/MfmImageUploader";
import { mfmFramedUrl } from "Projects/CC_cubi_clan_project/Utils/MfmService";
import DragSortableList from "Projects/CC_cubi_clan_project/GlobalComponents/DragSortableList/DragSortableList";
import RichTextEditor from "Projects/CC_cubi_clan_project/GlobalComponents/RichTextEditor/RichTextEditor";

// Imagen por defecto para roles/actores sin imagen
import profile_default_img from "images/profile_default.png";

const PORTRAIT_FORMAT_KEY = "ratio:3:4";

/**
 * AdminActorDetail
 *
 * Componente de detalle y edición de actores para el panel de administración.
 * Permite editar los campos básicos del actor (nombre, biografía, imagen,
 * estado) y gestionar los roles que interpreta en distintos proyectos.
 * Un rol vincula a un actor con un personaje dentro de una obra y puede
 * incluir una imagen propia.
 *
 * Al guardar el actor se envía un upsert a través del servicio
 * `upsertActors`. Al añadir roles se usa el servicio `upsertRoles`.
 */
class AdminActorDetail extends Component {
  constructor(props) {
    super(props);
    this.state = {
      idFromProps: false,
      actor: {},
      projectsList: [],
      personajesList: [],
      rolesByProject: [],
      loading: false,
      error: null,
      collapsedProjects: {},
      //Nuevo Rol
      newRole: null,
      // Modal de edición de rol
      showRoleEdit: false,
      selectedRole: null,
      // Modal de gestión de roles (orden y estado)
      showManageRoles: false,
      managedRoles: [],
      managedProyectos: [],
      // Búsqueda de usuario vinculado
      allUsers: [],
      userQuery: "",
      showUserList: false,
      filteredUsers: [],
      loadingUsers: false,
    };
    this.fileInputRef = React.createRef();
    this.userInputRef = React.createRef();
    this._mounted = false;
    this._loadActorRequestId = 0;
  }

  componentDidMount() {
    this._mounted = true;
    const id = this.getActorId();
    this.initActorData(id);
    this.loadProjectsList();
  }

  componentDidUpdate(prevProps) {
    if (this.props.actorId !== prevProps.actorId) {
      const id = this.getActorId();
      this.initActorData(id);
      this.loadProjectsList();
    }
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  /**
   * Obtiene el ID del actor a partir de props (si existe) o de la URL.
   */
  getActorId() {
    let id = this.getActorIdFromProps();
    if (!id) {
      id = this.getActorIdFromUrl();
    } else {
      this.setState({ idFromProps: true });
    }
    return id;
  }

  getActorIdFromProps() {
    return this.props.actorId;
  }

  getActorIdFromUrl() {
    const location = "" + window.location;
    const match = location.match(/actor\/([^/]+)$/);
    if (match) {
      return match[1];
    }
    return null;
  }

  /**
   * Inicializa los datos del actor. Si no hay ID se configura un actor
   * vacío; si existe ID se cargan sus datos del backend.
   */
  async initActorData(id) {
    if (id == null) {
      this.setState({
        actor: {
          name: "",
          bio: "",
          actorRole: "",
          imageUuid: null,
          status: "draft",
          roles: [],
        },
        personajesList: [],
        rolesByProject: [],
        newRole: null,
        userQuery: "",
        showUserList: false,
        filteredUsers: [],
      });
    } else {
      // Limpia estado derivado para evitar arrastre visual entre actores.
      this.setState({
        actor: {
          name: "",
          bio: "",
          actorRole: "",
          imageUuid: null,
          status: "draft",
          roles: [],
        },
        personajesList: [],
        rolesByProject: [],
        newRole: null,
        userQuery: "",
        showUserList: false,
        filteredUsers: [],
      });
      await this.loadActor(id);
    }
  }

  /**
   * Carga la lista de proyectos publicada para el selector de roles. Solo
   * se muestran los proyectos con estado `published`.
   */
  loadProjectsList() {
    this.props.onLoading?.(true);
    doFetch("MUGIN", "getProyectosLite", { method: "GET" })
      .then((r) => r.json())
      .then((data) => {
        const projects = data?.DATA || [];
        /*console.log(
          "JRML: projects: " +
            JSON.stringify(
              projects,
              (k, v) => (k === "imageBase64" ? String(v).substring(0, 10) : v),
              2
            )
        );*/
        this.setState({ projectsList: projects });
      })
      .catch((err) => {
        console.error("Error al cargar proyectos para roles:", err);
      })
      .finally((err) => {
        this.props.onLoading?.(false);
      });
  }

  /* ── Búsqueda de usuarios para vincular ── */

  loadUsers = async () => {
    if (this.state.allUsers.length > 0) return this.state.allUsers;
    this.setState({ loadingUsers: true });
    try {
      const resp = await doFetch("MUGIN", "getMUGINUsers", { method: "GET" });
      const json = await resp.json().catch(() => ({}));
      const users = Array.isArray(json?.DATA?.users) ? json.DATA.users : [];
      this.setState({ allUsers: users }, () => this.recomputeUserFilter());
      return users;
    } catch (e) {
      console.error("Error al cargar usuarios:", e);
      return [];
    } finally {
      this.setState({ loadingUsers: false });
    }
  };

  recomputeUserFilter = () => {
    const { allUsers, userQuery } = this.state;
    const q = (userQuery || "").trim().toLowerCase();
    const filtered = !q
      ? allUsers
      : allUsers.filter(
          (u) =>
            (u.nombre || "").toLowerCase().includes(q) ||
            (u.apellido || "").toLowerCase().includes(q) ||
            (u.apodo || "").toLowerCase().includes(q)
        );
    this.setState({ filteredUsers: filtered });
  };

  onUserQueryChange = (value) => {
    this.setState({ userQuery: value }, this.recomputeUserFilter);
  };

  openUserList = () => {
    this.loadUsers();
    this.setState({ showUserList: true }, this.recomputeUserFilter);
  };

  closeUserList = () => this.setState({ showUserList: false });

  selectUser = (user) => {
    const displayName =
      (user.nombre || "") + " " + (user.apellido || "") +
      (user.apodo ? " (@" + user.apodo + ")" : "");
    this.setState(
      (prev) => ({
        actor: { ...prev.actor, idUsuarioFK: user.iDUsuario ?? user.IDUsuario },
        userQuery: displayName.trim(),
        showUserList: false,
      }),
      () => this.userInputRef.current?.blur()
    );
  };

  clearUserLink = () => {
    this.setState((prev) => ({
      actor: { ...prev.actor, idUsuarioFK: null },
      userQuery: "",
    }));
  };

  onUserBlur = () => {
    const { userQuery, actor } = this.state;
    if (!userQuery.trim() && actor.idUsuarioFK) {
      // El usuario borró el campo — desvincular
      this.clearUserLink();
      return;
    }
    this.closeUserList();
  };

  onUserListMouseDown = (e) => e.preventDefault();

  /**
   * Carga los datos de un actor y sus roles. Se utiliza el servicio
   * `getActor`. Además se agrupan los roles por proyecto para facilitar la
   * visualización.
   */
  async loadActor(id) {
    const requestId = ++this._loadActorRequestId;
    this.props.onLoading?.(true);
    const method = `getActor?slug=${id}`;
    await doFetch("MUGIN", method, { method: "GET" })
      .then((r) => r.json())
      .then((data) => {
        if (!this._mounted || requestId !== this._loadActorRequestId) return;
        const actor = data?.DATA || {};
        const actorRoles = Array.isArray(actor.roles) ? actor.roles : [];
        const rolesByProject = actorRoles.reduce((acc, rol) => {
          const proyecto = rol.personaje.proyecto;
          const proyectoId = proyecto.idProyecto;

          if (!acc[proyectoId]) {
            acc[proyectoId] = {
              idProyecto: proyecto.idProyecto,
              slug: proyecto.slug,
              title: proyecto.title,
              roles: [],
            };
          }

          acc[proyectoId].roles.push(rol);

          return acc;
        }, {});
        this.setState({ actor: { ...actor, roles: actorRoles }, rolesByProject }, () => {
          if (!this._mounted || requestId !== this._loadActorRequestId) return;
          // Si el actor tiene usuario vinculado, cargar usuarios para mostrar el nombre
          if (actor.idUsuarioFK) {
            this.loadUsers().then((users) => {
              if (!this._mounted || requestId !== this._loadActorRequestId) return;
              const u = (users || []).find(
                (u) => (u.iDUsuario ?? u.IDUsuario) == actor.idUsuarioFK
              );
              if (u) {
                const displayName =
                  (u.nombre || "") + " " + (u.apellido || "") +
                  (u.apodo ? " (@" + u.apodo + ")" : "");
                this.setState({ userQuery: displayName.trim() });
              } else {
                this.setState({ userQuery: "Usuario #" + actor.idUsuarioFK });
              }
            });
          } else {
            this.setState({ userQuery: "" });
          }
        });
      })
      .catch((err) => {
        console.error("Error al cargar el actor:", err);
        toast.show({
          message: "Ha ocurrido un error al cargar el actor",
          type: "danger",
          position: "bottom-center",
          size: "wide",
          animation: "slide",
          timeout: 5000,
        });
      })
      .finally(() => {
        if (this._mounted && requestId === this._loadActorRequestId) {
          this.props.onLoading?.(false);
        }
      });
  }

  /**
   * Actualiza un campo del actor en el estado.
   */
  updateActorField = (field, value) => {
    this.setState((s) => ({ actor: { ...s.actor, [field]: value } }));
  };

  /**
   * Maneja la selección de un proyecto en el formulario de alta de roles.
   * Una vez seleccionado se cargan los personajes de esa obra.
   */
  handleSelectProject = (e) => {
    const idProyecto = e.target.value;
    this.setState((s) => ({
      newRole: { ...s.newRole, idProyecto: idProyecto },
    }));
    if (!idProyecto) return;
    // Cargar personajes del proyecto seleccionado
    const proyecto = this.state.projectsList.find(
      (project) => project.idProyecto == idProyecto
    );
    if (proyecto) {
      this.setState({ personajesList: proyecto.personajes });
    }
  };

  /**
   * Maneja la selección de personaje en el formulario de alta de roles.
   */
  handleSelectPersonaje = (e) => {
    this.setState((s) => ({
      newRole: { ...s.newRole, idPersonaje: e.target.value },
    }));
  };

  /**
   * Maneja la selección de imagen para un nuevo rol. Convierte el archivo
   * a Base64 para enviarlo al backend.
   */
  handleRoleImageChange(uuid) {
    this.setState((s) => ({ newRole: { ...s.newRole, imageUuid: uuid } }));
  }

  /**
   * Envía al backend la creación de un nuevo rol asociando este actor con
   * un personaje en una obra determinada. Requiere haber seleccionado
   * proyecto y personaje.
   */
  addRole = () => {
    const { actor, newRole, personajesList } = this.state;
    const personaje = personajesList.find(
      (pers) => pers.idPersonaje == newRole.idPersonaje
    );
    if (!actor.idActor) {
      toast.show({
        message: "Debe guardar primero el actor antes de añadir roles",
        type: "warning",
        position: "bottom-center",
        size: "wide",
        animation: "slide",
        timeout: 5000,
      });
      return;
    }
    if (!newRole.idProyecto || !newRole.idPersonaje || !personaje) {
      toast.show({
        message: "Debe seleccionar una obra y un personaje",
        type: "warning",
        position: "bottom-center",
        size: "wide",
        animation: "slide",
        timeout: 4000,
      });
      return;
    }

    const alreadyAssigned = (actor.roles || []).some(
      (r) =>
        Number(r?.personaje?.idPersonaje) === Number(personaje.idPersonaje)
    );
    if (alreadyAssigned) {
      toast.show({
        message: "Este actor ya tiene asignado ese personaje en la obra seleccionada",
        type: "warning",
        position: "bottom-center",
        size: "wide",
        animation: "slide",
        timeout: 4500,
      });
      return;
    }

    const payload = [
      {
        actor: { idActor: actor.idActor },
        personaje: { idPersonaje: personaje.idPersonaje },
        status: "published",
        sortOrder: 0,
        imageUuid: newRole.imageUuid || null,
      },
    ];
    this.props.onLoading?.(true);
    doFetch("MUGIN", "upsertRoles", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((r) => r.json().catch(() => ({})))
      .then((res) => {
        if (res?.STATE === "SUCCESS") {
          toast.show({
            message: "Rol añadido correctamente",
            type: "success",
            position: "bottom-center",
            size: "wide",
            animation: "slide",
            timeout: 3000,
          });
          // Vaciar campos y recargar actor
          this.setState((s) => ({
            newRole: {
              ...s.newRole,
              idProyecto: "",
              idPersonaje: "",
              imageUuid: null,
            },
          }));
          this.loadActor(actor.slug);
        } else {
          toast.show({
            message:
              res?.MESSAGE ||
              "No se pudo crear el rol. Compruebe los datos e inténtelo de nuevo.",
            type: "danger",
            position: "bottom-center",
            size: "wide",
            animation: "slide",
            timeout: 5000,
          });
        }
      })
      .catch((err) => {
        console.error("Error al crear rol:", err);
        toast.show({
          message: "Ha ocurrido un error al crear el rol",
          type: "danger",
          position: "bottom-center",
          size: "wide",
          animation: "slide",
          timeout: 5000,
        });
      })
      .finally(() => {
        this.props.onLoading?.(false);
      });
  };

  /**
   * Elimina un rol existente mediante el servicio `deleteRole?idRol=`.
   */
  removeRole = (idRol) => {
    if (!idRol) return;
    doFetch("MUGIN", `deleteRole?IDRole=${idRol}`, { method: "DELETE" })
      .then((r) => r.json())
      .then((data) => {
        if (data && data.STATE === "SUCCESS") {
          toast.show({
            message: "Rol eliminado",
            type: "success",
            position: "bottom-center",
            size: "wide",
            animation: "slide",
            timeout: 3000,
          });
          this.loadActor(this.state.actor.slug);
        } else {
          toast.show({
            message:
              data?.MESSAGE || "Error al eliminar rol, inténtelo de nuevo.",
            type: "danger",
            position: "bottom-center",
            size: "wide",
            animation: "slide",
            timeout: 5000,
          });
        }
      })
      .catch((err) => {
        console.error("Error al eliminar rol:", err);
        toast.show({
          message: "Ha ocurrido un error al eliminar el rol",
          type: "danger",
          position: "bottom-center",
          size: "wide",
          animation: "slide",
          timeout: 5000,
        });
      });
  };

  /**
   * Guarda el actor actual usando upsertActors. Actualiza el ID en caso de
   * que sea una creación y notifica al padre tras guardar.
   */
  saveActor = () => {
    const { actor, userQuery } = this.state;
    // Race condition guard: si userQuery está vacío pero setState de clearUserLink
    // aún no se ha procesado, forzar idUsuarioFK a null.
    const actorToSave = !userQuery.trim() && actor.idUsuarioFK
      ? { ...actor, idUsuarioFK: null }
      : actor;
    // No enviar campos indefinidos al backend
    this.props.onLoading?.(true);
    doFetch("MUGIN", "upsertActors", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify([actorToSave]),
    })
      .then((r) => r.json().catch(() => ({})))
      .then((res) => {
        if (res?.STATE === "SUCCESS") {
          const saved =
            res?.DATA && Array.isArray(res.DATA) && res.DATA[0]
              ? res.DATA[0]
              : null;
          if (saved && !actor.idActor) {
            // Recargar el actor desde el backend para obtener idActor y slug
            // correctamente generados, evitando posibles desincronías de estado.
            const newSlug = saved.slug || saved.Slug;
            if (newSlug) {
              this.loadActor(newSlug);
            }
          }
          toast.show({
            message: "Actor guardado correctamente",
            type: "success",
            position: "bottom-center",
            size: "wide",
            animation: "slide",
            timeout: 3000,
          });
          this.props.onSave && this.props.onSave();
        } else {
          toast.show({
            message:
              res?.MESSAGE ||
              "No se pudo guardar el actor. Revise los datos e inténtelo de nuevo.",
            type: "danger",
            position: "bottom-center",
            size: "wide",
            animation: "slide",
            timeout: 5000,
          });
        }
      })
      .catch((err) => {
        console.error("Error al guardar actor:", err);
        toast.show({
          message: "Ha ocurrido un error al guardar el actor",
          type: "danger",
          position: "bottom-center",
          size: "wide",
          animation: "slide",
          timeout: 5000,
        });
      })
      .finally(() => {
        this.props.onLoading?.(false);
      });
  };

  /**
   * Maneja la subida de imagen del actor. Convierte el fichero a Base64
   * eliminando el prefijo data:image/...
   */
  handleImageChange(base64) {
    this.setState((s) => ({ actor: { ...s.actor, imageBase64: base64 } }));
  }

  /**
   * Actualiza la imagen del rol en edición. Recibe una cadena Base64
   * (sin cabecera data:image/...). Se almacena en selectedRole.
   */
  handleRoleEditImageChange = (uuid) => {
    this.setState((s) => ({
      selectedRole: { ...s.selectedRole, imageUuid: uuid, imageBase64: null },
    }));
  };

  /**
   * Cambia el estado (status) del actor entre draft y published.
   */
  toggleStatus = () => {
    this.setState((s) => ({
      actor: {
        ...s.actor,
        status: s.actor.status === "published" ? "draft" : "published",
      },
    }));
  };

  /**
   * Alterna la visibilidad de la lista de roles de un proyecto concreto.
   */
  toggleProjectCollapse = (projId) => {
    this.setState((s) => ({
      collapsedProjects: {
        ...s.collapsedProjects,
        [projId]: !s.collapsedProjects[projId],
      },
    }));
  };

  /**
   * Devuelve la imagen adecuada para un rol aplicando la política de
   * fallback: imagen del rol → imagen del personaje → imagen del actor
   * → imagen por defecto. Convierte cadenas Base64 a URI si es necesario.
   */
  getRoleImage = (rol) => {
    const { actor } = this.state;
    // Prioridad: imageUuid del rol > imageUuid personaje > imageUuid actor
    if (rol?.imageUuid) return mfmFramedUrl(rol.imageUuid, 270, 360, PORTRAIT_FORMAT_KEY);
    if (rol?.personaje?.imageUuid) return mfmFramedUrl(rol.personaje.imageUuid, 270, 360, PORTRAIT_FORMAT_KEY);
    if (actor?.imageUuid) return mfmFramedUrl(actor.imageUuid, 270, 360, PORTRAIT_FORMAT_KEY);
    return profile_default_img;
  };

  /**
   * Devuelve la imagen del personaje asociado al rol (sin fallback al rol).
   */
  getPersonajeImage = (rol) => {
    if (rol?.personaje?.imageUuid) return mfmFramedUrl(rol.personaje.imageUuid, 270, 360, PORTRAIT_FORMAT_KEY);
    return profile_default_img;
  };

  /**
   * Devuelve la imagen propia del rol (sin fallback al personaje ni al actor).
   */
  getRoleOwnImage = (rol) => {
    if (rol?.imageUuid) return mfmFramedUrl(rol.imageUuid, 270, 360, PORTRAIT_FORMAT_KEY);
    return profile_default_img;
  };

  /**
   * Maneja la apertura del modal de edición de un rol concreto. Recibe el
   * rol y lo almacena en el estado, mostrando el editor.
   */
  openRoleEdit = (rol) => {
    if (!rol) return;
    this.setState({ selectedRole: { ...rol }, showRoleEdit: true });
  };

  /**
   * Guarda la modificación de la imagen del rol en el backend usando
   * upsertRoles. Si tiene éxito se recarga el actor.
   */
  saveRoleEdit = () => {
    const { selectedRole, actor } = this.state;
    if (!selectedRole || !selectedRole.idRol) return;
    const payload = [selectedRole];
    this.props.onLoading?.(true);
    doFetch("MUGIN", "upsertRoles", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((r) => r.json().catch(() => ({})))
      .then((res) => {
        if (res?.STATE === "SUCCESS") {
          toast.show({
            message: "Rol actualizado correctamente",
            type: "success",
            position: "bottom-center",
            size: "wide",
            animation: "slide",
            timeout: 3000,
          });
          // Recargar actor para reflejar cambios
          this.loadActor(actor.slug);
          this.setState({ showRoleEdit: false, selectedRole: null });
        } else {
          toast.show({
            message:
              res?.MESSAGE ||
              "No se pudo actualizar el rol. Inténtelo de nuevo.",
            type: "danger",
            position: "bottom-center",
            size: "wide",
            animation: "slide",
            timeout: 5000,
          });
        }
      })
      .catch((err) => {
        console.error("Error al actualizar rol:", err);
        toast.show({
          message: "Ha ocurrido un error al actualizar el rol",
          type: "danger",
          position: "bottom-center",
          size: "wide",
          animation: "slide",
          timeout: 5000,
        });
      })
      .finally(() => {
        this.props.onLoading?.(false);
      });
  };

  closeRoleEdit = () => {
    this.setState({ showRoleEdit: false, selectedRole: null });
  };

  /**
   * Abre la ventana de gestión de roles. Clona las roles actuales del actor
   * ordenándolos por sortOrder. Se usa para reordenar y cambiar estado.
   */
  openManageRolesModal = () => {
    const { actor, rolesByProject } = this.state;
    if (!actor || !Array.isArray(actor.roles)) {
      this.setState({ managedRoles: [], managedProyectos: [], showManageRoles: true });
      return;
    }

    // Roles con flags de destacado
    const managedRoles = actor.roles.slice().map((r) => ({
      ...r,
      enDestacado: Boolean(r.sortOrder && r.sortOrder > 0),
      destacadoOrder: r.sortOrder && r.sortOrder > 0 ? r.sortOrder : 0,
    }));

    // Proyectos en orden guardado en localStorage
    const actorSlug = actor.slug;
    let storedOrder = [];
    try {
      const stored = localStorage.getItem(`actorObrasOrder_${actorSlug}`);
      if (stored) storedOrder = JSON.parse(stored);
    } catch {}

    const managedProyectos = Object.entries(rolesByProject)
      .map(([idProyecto, proy]) => {
        const id = Number(idProyecto);
        const idx = storedOrder.indexOf(id);
        return {
          idProyecto: id,
          title: proy.title,
          slug: proy.slug,
          sortOrder: idx >= 0 ? idx + 1 : 999,
        };
      })
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((p, i) => ({ ...p, sortOrder: i + 1 }));

    this.setState({ managedRoles, managedProyectos, showManageRoles: true });
  };

  closeManageRolesModal = () => {
    this.setState({ showManageRoles: false, managedRoles: [], managedProyectos: [] });
  };

  /**
   * Alterna el estado de publicación de un rol en el modal (published/draft).
   */
  toggleRoleStatus = (rol) => {
    if (!rol) return;
    const { managedRoles } = this.state;
    const updated = managedRoles.map((r) => {
      if (r.idRol === rol.idRol) {
        const newStatus = r.status === "published" ? "draft" : "published";
        return { ...r, status: newStatus };
      }
      return r;
    });
    this.setState({ managedRoles: updated });
  };

  /**
   * Alterna si un rol aparece en la sección "Roles destacados".
   */
  toggleRoleDestacado = (rol) => {
    if (!rol) return;
    const { managedRoles } = this.state;
    const updated = managedRoles.map((r) => {
      if (r.idRol === rol.idRol) {
        if (r.enDestacado) {
          return { ...r, enDestacado: false, destacadoOrder: 0 };
        } else {
          const maxOrder = managedRoles
            .filter((x) => x.enDestacado)
            .reduce((max, x) => Math.max(max, x.destacadoOrder || 0), 0);
          return { ...r, enDestacado: true, destacadoOrder: maxOrder + 1 };
        }
      }
      return r;
    });
    this.setState({ managedRoles: updated });
  };

  /**
   * Actualiza el orden de los roles destacados tras un drag&drop.
   */
  handleDestacadosReorder = (updatedDestacados) => {
    const orderMap = {};
    updatedDestacados.forEach((r) => { orderMap[r.idRol] = r.sortOrder; });
    const { managedRoles } = this.state;
    const updated = managedRoles.map((r) =>
      orderMap[r.idRol] !== undefined ? { ...r, destacadoOrder: orderMap[r.idRol] } : r
    );
    this.setState({ managedRoles: updated });
  };

  /**
   * Actualiza el orden de las obras tras un drag&drop.
   */
  handleProyectosReorder = (updated) => {
    this.setState({ managedProyectos: updated });
  };

  /**
   * Guarda el nuevo orden y estado de los roles editados. Envía un
   * upsertRoles con los campos idRol, sortOrder y status. Si tiene
   * éxito se actualiza el actor y se cierra el modal.
   */
  saveRolesChanges = () => {
    const { managedRoles, managedProyectos, actor } = this.state;
    if (!Array.isArray(managedRoles) || managedRoles.length === 0) {
      this.setState({ showManageRoles: false });
      return;
    }

    // Calcular sortOrder final: destacados 1..N según su orden, el resto 0
    const destacados = managedRoles
      .filter((r) => r.enDestacado)
      .sort((a, b) => (a.destacadoOrder || 999) - (b.destacadoOrder || 999));
    const destacadoOrderMap = {};
    destacados.forEach((r, i) => { destacadoOrderMap[r.idRol] = i + 1; });

    // Incluir todos los campos del rol para que toRoleEntity no los nullee al hacer merge
    const payload = managedRoles.map((r) => ({
      idRol: r.idRol,
      sortOrder: destacadoOrderMap[r.idRol] ?? 0,
      status: r.status,
      imageUuid: r.imageUuid ?? null,
      imageBase64: r.imageBase64 ?? null,
      billingOrder: r.billingOrder ?? null,
      notes: r.notes ?? null,
      actor: r.actor?.idActor != null ? { idActor: r.actor.idActor } : null,
      personaje: r.personaje?.idPersonaje != null ? { idPersonaje: r.personaje.idPersonaje } : null,
    }));

    // Guardar orden de obras en localStorage
    const actorSlug = actor.slug;
    try {
      const proyectosOrder = managedProyectos.map((p) => p.idProyecto);
      localStorage.setItem(`actorObrasOrder_${actorSlug}`, JSON.stringify(proyectosOrder));
    } catch {}

    this.props.onLoading?.(true);
    doFetch("MUGIN", "upsertRoles", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((r) => r.json().catch(() => ({})))
      .then((res) => {
        if (res?.STATE === "SUCCESS") {
          toast.show({
            message: "Roles actualizados correctamente",
            type: "success",
            position: "bottom-center",
            size: "wide",
            animation: "slide",
            timeout: 3000,
          });
          // Recargar actor para refrescar lista
          this.loadActor(actor.slug);
          this.setState({ showManageRoles: false, managedRoles: [], managedProyectos: [] });
        } else {
          toast.show({
            message:
              res?.MESSAGE ||
              "No se pudo actualizar el listado de roles. Inténtelo de nuevo.",
            type: "danger",
            position: "bottom-center",
            size: "wide",
            animation: "slide",
            timeout: 5000,
          });
        }
      })
      .catch((err) => {
        console.error("Error al actualizar roles:", err);
        toast.show({
          message: "Ha ocurrido un error al guardar el orden de los roles",
          type: "danger",
          position: "bottom-center",
          size: "wide",
          animation: "slide",
          timeout: 5000,
        });
      })
      .finally(() => {
        this.props.onLoading?.(false);
      });
  };

  render() {
    const {
      actor,
      projectsList,
      personajesList,
      rolesByProject,
      newRole,
      collapsedProjects,
      showRoleEdit,
      selectedRole,
      showManageRoles,
      managedRoles,
      managedProyectos,
    } = this.state;

    return (
      <div className="admin-actor-detail">
        {/* Cabecera con botones */}
        <div className="aad-fixed-buttons">
          <MDBBtn className="mugin-btn-primary" size="sm" onClick={this.saveActor}>
            Guardar
          </MDBBtn>
          <MDBBtn
            color="secondary"
            size="sm"
            onClick={() => this.props.onClose && this.props.onClose()}
          >
            Cerrar
          </MDBBtn>
        </div>

        {/* Datos básicos del actor */}
        <h3 className="mb-3">
          {actor.idActor ? "Editar actor" : "Nuevo actor"}
        </h3>
        <div className="aad-header-layout mb-3">
          <div className="aad-photo-col">
            {/* Vista previa de la imagen */}
            <div className="aad-actor-image">
              <MfmImageUploader
                uuid={actor.imageUuid || null}
                appCode="MUGIN"
                library="Miembros"
                size="medium"
                mediaType="image"
                mediaFormat="3/4"
                mediaFrameSize="216x288"
                allowPicker
                onUpload={(result) => this.updateActorField("imageUuid", result.uuid)}
                onDelete={() => this.updateActorField("imageUuid", null)}
                style={{ width: "100%", height: "100%", borderRadius: 8 }}
              />
            </div>
          </div>
          <div className="aad-fields-col">
            <div className="mb-2">
              <label htmlFor="actor-name" className="form-label">
                Nombre
              </label>
              <input
                id="actor-name"
                type="text"
                className="form-control form-control-sm"
                value={actor.name || ""}
                onChange={(e) => this.updateActorField("name", e.target.value)}
              />
            </div>
            <div className="mb-2">
              <label htmlFor="actor-role" className="form-label">
                Rol / cargo
              </label>
              <input
                id="actor-role"
                type="text"
                list="actor-role-suggestions"
                className="form-control form-control-sm"
                value={actor.actorRole || ""}
                onChange={(e) => this.updateActorField("actorRole", e.target.value)}
                placeholder="Ej: Actor, Directora, Técnico de sonido…"
              />
              <datalist id="actor-role-suggestions">
                <option value="Actor" />
                <option value="Actriz" />
                <option value="Director" />
                <option value="Directora" />
                <option value="Director técnico" />
                <option value="Técnico de sonido" />
                <option value="Técnico de iluminación" />
                <option value="Coreógrafo" />
                <option value="Coreógrafa" />
                <option value="Productor" />
                <option value="Productora" />
              </datalist>
            </div>
            <div className="d-flex align-items-center mt-2 mb-2">
              <span className="me-2">Estado:</span>
              <MDBSwitch
                id="actor-status"
                checked={actor.status === "published"}
                onChange={this.toggleStatus}
                label={actor.status === "published" ? "Activo" : "Inactivo"}
              />
            </div>
            <div className="mb-2">
              <label htmlFor="actor-usuario" className="form-label">
                Usuario vinculado
              </label>
              <div className="user-typeahead">
                <MDBInput
                  inputRef={this.userInputRef}
                  label="Buscar usuario"
                  value={this.state.userQuery}
                  onChange={(e) => this.onUserQueryChange(e.target.value)}
                  onFocus={this.openUserList}
                  onBlur={this.onUserBlur}
                  disabled={this.state.loadingUsers}
                />
                {actor.idUsuarioFK && (
                  <button
                    type="button"
                    className="user-clear-btn"
                    title="Desvincular usuario"
                    onClick={this.clearUserLink}
                  >
                    ×
                  </button>
                )}
                {this.state.showUserList && (
                  <div className="user-list" onMouseDown={this.onUserListMouseDown}>
                    {this.state.filteredUsers.length === 0 ? (
                      <div className="user-empty">Sin resultados</div>
                    ) : (
                      this.state.filteredUsers.map((u) => (
                        <div
                          key={u.iDUsuario ?? u.IDUsuario}
                          className="user-item"
                          onClick={() => this.selectUser(u)}
                        >
                          <div className="user-thumb">
                            {u.userImg ? (
                              <img alt="user" src={u.userImg} />
                            ) : (
                              <div className="user-noimg" />
                            )}
                          </div>
                          <div className="user-data">
                            <div className="user-name">
                              {u.nombre} {u.apellido}
                            </div>
                            {u.apodo && (
                              <div className="user-apodo">@{u.apodo}</div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Biografía — a todo el ancho */}
        <div className="mb-3">
          <label className="form-label">Biografía</label>
          <RichTextEditor
            key={`actor-bio-${actor.idActor || actor.slug || "new"}`}
            initialHtml={actor.bio || ""}
            onChangeHtml={(html) => this.updateActorField("bio", html)}
            height={250}
            appCode="MUGIN"
          />
        </div>

        {/* Sección de roles */}
        <div className="aad-roles-header">
          <h4>Roles</h4>
          {/* Botón para gestionar orden y estado de roles */}
          {actor && actor.roles && actor.roles.length > 0 && (
            <MDBBtn
              size="sm"
              color="secondary"
              outline
              onClick={this.openManageRolesModal}
            >
              Gestionar roles
            </MDBBtn>
          )}
        </div>
        {/* Lista agrupada por proyecto */}
        <div className="aad-roles-content">
          {/* Lista agrupada por proyecto */}
          <div className="aad-roles-list-col">
            {actor && actor.roles && actor.roles.length === 0 && (
              <p className="fst-italic">Este actor aún no tiene roles asignados.</p>
            )}
            {actor && actor.roles && actor.roles.length > 0 && (
              <div className="aad-roles-list">
                {Object.entries(rolesByProject).map(([idProyecto, proyecto]) => (
                  <div key={idProyecto} className="aad-role-project">
                    <div className="aad-role-project-header d-flex align-items-center justify-content-between">
                      <span
                        className="aad-role-project-title"
                        onClick={() => this.toggleProjectCollapse(idProyecto)}
                        style={{ cursor: "pointer" }}
                      >
                        {collapsedProjects[idProyecto] ? "▶" : "▼"} {proyecto.title}
                      </span>
                      <MDBBtn
                        size="sm"
                        color="info"
                        outline
                        onClick={() => {
                          window.open(`/project/${proyecto.slug}`, "_blank");
                        }}
                      >
                        Ver obra
                      </MDBBtn>
                    </div>
                    <MDBCollapse
                      key={`${idProyecto}-${proyecto.roles.length}`}
                      show={!collapsedProjects[idProyecto]}
                    >
                      <ul className="aad-roles-ul list-unstyled ms-2">
                        {proyecto.roles.map((rol) => (
                          <li
                            key={rol.idRol}
                            className="d-flex align-items-center justify-content-between aad-role-item"
                          >
                            <div className="d-flex align-items-center">
                              <img
                                src={this.getRoleImage(rol)}
                                alt="rol"
                                className="aad-role-thumb me-2"
                              />
                              <span>
                                {rol.personaje?.name ||
                                  rol.personajeName ||
                                  "Unknown"}
                              </span>
                            </div>
                            <div className="d-flex align-items-center gap-1">
                              <MDBBtn
                                size="sm"
                                color="secondary"
                                outline
                                onClick={() => this.openRoleEdit(rol)}
                              >
                                Editar
                              </MDBBtn>
                              <MDBBtn
                                size="sm"
                                color="danger"
                                outline
                                onClick={() => this.removeRole(rol.idRol)}
                              >
                                Quitar
                              </MDBBtn>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </MDBCollapse>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Formulario para añadir nuevo rol */}
          <div className="aad-new-role">
            <h5>Añadir nuevo rol</h5>
            <div className="mb-3">
              <label className="form-label">Obra</label>
              <select
                className="form-select form-select-sm"
                value={newRole?.idProyecto}
                onChange={this.handleSelectProject}
              >
                <option value="">Seleccione una obra</option>
                {projectsList.map((p) => (
                  <option key={p.idProyecto} value={p.idProyecto}>
                    {p.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label">Personaje</label>
              <select
                className="form-select form-select-sm"
                value={newRole?.idPersonaje}
                onChange={this.handleSelectPersonaje}
                disabled={!newRole?.idProyecto}
              >
                <option value="">Seleccione un personaje</option>
                {personajesList.map((p) => (
                  <option key={p.idPersonaje} value={p.idPersonaje}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-3">
              <MfmImageUploader
                uuid={newRole?.imageUuid || null}
                appCode="MUGIN"
                library="roles"
                size="medium"
                mediaType="image"
                mediaFormat="3/4"
                mediaFrameSize="270x360"
                allowPicker
                onUpload={(result) => this.handleRoleImageChange(result.uuid)}
                onDelete={() => this.handleRoleImageChange(null)}
                style={{ width: "100%", aspectRatio: "3/4", borderRadius: 8 }}
              />
            </div>
            <MDBBtn size="sm" className="mugin-btn-primary" onClick={this.addRole}>
              Añadir rol
            </MDBBtn>
          </div>
        </div>

        {/* Modal para editar un rol */}
        {showRoleEdit && selectedRole && (
          <div className="aad-modal-overlay" onClick={this.closeRoleEdit}>
            <div className="aad-modal aad-edit-role-modal" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="aad-edit-role-header">
                <h5>Editar rol</h5>
                <MDBBtn
                  className="btn-close"
                  color="none"
                  onClick={this.closeRoleEdit}
                  aria-label="Cerrar"
                />
              </div>

              <div className="aad-edit-role-body">
                {/* Info del proyecto y personaje */}
                <div className="aad-edit-role-info">
                  <div className="aad-edit-role-field">
                    <span className="aad-edit-role-label">Proyecto</span>
                    <span className="aad-edit-role-value">
                      {selectedRole.personaje?.proyecto?.title || "—"}
                    </span>
                  </div>
                  <div className="aad-edit-role-field">
                    <span className="aad-edit-role-label">Personaje</span>
                    <span className="aad-edit-role-value">
                      {selectedRole.personaje?.name ||
                        selectedRole.personajeName ||
                        "—"}
                    </span>
                  </div>
                </div>

                {/* Imágenes lado a lado: personaje (readonly) + rol (editable) */}
                <div className="aad-edit-role-images">
                  <div className="aad-edit-role-img-col">
                    <span className="aad-edit-role-label">Personaje</span>
                    <div className="aad-edit-role-portrait">
                      <img
                        src={this.getPersonajeImage(selectedRole)}
                        alt={selectedRole.personaje?.name || "Maestro"}
                        style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 6 }}
                      />
                    </div>
                  </div>
                  <div className="aad-edit-role-img-col">
                    <span className="aad-edit-role-label">Rol</span>
                    <div className="aad-edit-role-portrait">
                      <MfmImageUploader
                        uuid={selectedRole?.imageUuid || null}
                        appCode="MUGIN"
                        library="roles"
                        size="medium"
                        mediaType="image"
                        mediaFormat="3/4"
                        mediaFrameSize="270x360"
                        allowPicker
                        onUpload={(result) => this.handleRoleEditImageChange(result.uuid)}
                        onDelete={() => this.handleRoleEditImageChange(null)}
                        style={{ width: "100%", height: "100%", borderRadius: 6 }}
                      />
                    </div>
                  </div>
                </div>

                {/* Notas del rol */}
                <div className="aad-edit-role-notes">
                  <label className="aad-edit-role-label mb-2">
                    Notas del rol
                  </label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={selectedRole.notes || ""}
                    onChange={(e) =>
                      this.setState((s) => ({
                        selectedRole: {
                          ...s.selectedRole,
                          notes: e.target.value,
                        },
                      }))
                    }
                    placeholder="Notas sobre el rol del actor en esta obra..."
                  />
                </div>
              </div>

              {/* Footer con acciones */}
              <div className="aad-edit-role-footer">
                <MDBBtn
                  size="sm"
                  color="secondary"
                  outline
                  onClick={this.closeRoleEdit}
                >
                  Cancelar
                </MDBBtn>
                <MDBBtn
                  size="sm"
                  className="mugin-btn-primary"
                  onClick={this.saveRoleEdit}
                >
                  Guardar cambios
                </MDBBtn>
              </div>
            </div>
          </div>
        )}

        {/* Modal para gestionar roles (orden y estado) */}
        {showManageRoles && (
          <div className="aad-modal-overlay" onClick={this.closeManageRolesModal}>
            <div className="aad-modal aad-manage-roles-modal" onClick={(e) => e.stopPropagation()}>
              <h5 className="mb-3">Gestionar roles</h5>

              {/* ── Roles destacados ── */}
              <div className="aad-manage-section">
                <h6 className="aad-manage-section-title">Roles destacados</h6>
                {managedRoles.filter((r) => r.enDestacado).length === 0 ? (
                  <p className="text-muted fst-italic small mb-0">
                    Ningún rol marcado como destacado.
                  </p>
                ) : (
                  <DragSortableList
                    items={managedRoles
                      .filter((r) => r.enDestacado)
                      .map((r) => ({ ...r, sortOrder: r.destacadoOrder }))}
                    orderProp="sortOrder"
                    getKey={(item, idx) => item.idRol ?? idx}
                    renderItem={(item) => (
                      <div className="aad-manage-role-item d-flex align-items-center">
                        <img
                          src={this.getRoleImage(item)}
                          alt="rol"
                          className="aad-role-thumb me-2"
                        />
                        <div className="aad-manage-role-info">
                          <div className="fw-bold">
                            {item.personaje?.name || item.personajeName || ""}
                          </div>
                          <div className="text-muted small">
                            {item.personaje?.proyecto?.title || item.projectTitle || ""}
                          </div>
                        </div>
                      </div>
                    )}
                    onChange={this.handleDestacadosReorder}
                  />
                )}
              </div>

              {/* ── Orden de obras ── */}
              <div className="aad-manage-section">
                <h6 className="aad-manage-section-title">Orden de obras</h6>
                {managedProyectos.length === 0 ? (
                  <p className="text-muted fst-italic small mb-0">Sin obras.</p>
                ) : (
                  <DragSortableList
                    items={managedProyectos}
                    orderProp="sortOrder"
                    getKey={(item, idx) => item.idProyecto ?? idx}
                    renderItem={(item) => (
                      <div className="aad-manage-role-item d-flex align-items-center">
                        <span className="fw-semibold">{item.title}</span>
                      </div>
                    )}
                    onChange={this.handleProyectosReorder}
                  />
                )}
              </div>

              {/* ── Todos los roles ── */}
              <div className="aad-manage-section">
                <h6 className="aad-manage-section-title">Roles</h6>
                {managedRoles.length === 0 && (
                  <p className="text-muted fst-italic small">No hay roles para gestionar.</p>
                )}
                {managedRoles.map((item) => (
                  <div
                    key={item.idRol}
                    className="aad-manage-role-item d-flex align-items-center justify-content-between"
                  >
                    <div className="d-flex align-items-center">
                      <img
                        src={this.getRoleImage(item)}
                        alt="rol"
                        className="aad-role-thumb me-2"
                      />
                      <div className="aad-manage-role-info">
                        <div className="fw-bold">
                          {item.personaje?.name || item.personajeName || ""}
                        </div>
                        <div className="text-muted small">
                          {item.personaje?.proyecto?.title || item.projectTitle || ""}
                        </div>
                      </div>
                    </div>
                    <div className="aad-manage-role-actions d-flex align-items-center gap-3">
                      <MDBSwitch
                        id={`role-switch-hab-${item.idRol}`}
                        checked={item.status === "published"}
                        onChange={() => this.toggleRoleStatus(item)}
                        label="Hab."
                        title={item.status === "published" ? "Habilitado" : "Deshabilitado"}
                      />
                      <MDBSwitch
                        id={`role-switch-dest-${item.idRol}`}
                        checked={item.enDestacado === true}
                        onChange={() => this.toggleRoleDestacado(item)}
                        label="Dest."
                        title={item.enDestacado ? "En destacados" : "No en destacados"}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="d-flex justify-content-end gap-2 mt-3">
                <MDBBtn
                  size="sm"
                  color="secondary"
                  outline
                  onClick={this.closeManageRolesModal}
                >
                  Cancelar
                </MDBBtn>
                <MDBBtn
                  size="sm"
                  className="mugin-btn-primary"
                  onClick={this.saveRolesChanges}
                >
                  Guardar cambios
                </MDBBtn>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default AdminActorDetail;
